import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Wallet, DollarSign, CheckCircle2, FileDown, Search, Landmark } from "lucide-react";
import api, { money } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const GTQ = (n) => money("GTQ", n);

export const ReceivablesSummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/invoices/receivables-summary")
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  const s = data?.summary || { billed: 0, collected: 0, pending: 0, retained: 0, b0_30: 0, b31_60: 0, b60p: 0 };
  const clients = useMemo(() => {
    const rows = data?.clients || [];
    const term = q.trim().toLowerCase();
    return term ? rows.filter((c) => (c.client_name || "").toLowerCase().includes(term)) : rows;
  }, [data, q]);

  const cards = [
    { label: "Facturado", value: GTQ(s.billed), Icon: DollarSign, cls: "text-blue-500 border-blue-500/20 bg-blue-500/10" },
    { label: "Cobrado", value: GTQ(s.collected), Icon: CheckCircle2, cls: "text-green-500 border-green-500/20 bg-green-500/10" },
    { label: "Retenido IVA", value: GTQ(s.retained), Icon: Landmark, cls: "text-indigo-500 border-indigo-500/20 bg-indigo-500/10" },
    { label: "Saldo pendiente", value: GTQ(s.pending), Icon: Wallet, cls: "text-amber-500 border-amber-500/20 bg-amber-500/10" },
  ];

  const exportCSV = () => {
    const headers = ["Cliente", "# Fac.", "Facturado", "Cobrado", "Retenido IVA", "Saldo", "0-30", "31-60", "+60", "Mora max (d)"];
    const lines = (data?.clients || []).map((c) => [c.client_name, c.invoice_count, c.billed, c.collected, c.retained, c.pending, c.b0_30, c.b31_60, c.b60p, c.max_overdue]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = ["\ufeff" + headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cuentas-por-cobrar-${new Date(Date.now()-216e5).toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="receivables-summary">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-xl border p-4 ${c.cls}`}>
            <div className="flex items-center gap-2 mb-2"><c.Icon className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-widest">{c.label}</span></div>
            <div className="text-2xl font-bold font-mono">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-sm border border-border p-3"><p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">0-30 días</p><p className="text-lg font-bold font-mono text-green-700">{GTQ(s.b0_30)}</p></div>
        <div className="rounded-sm border border-border p-3"><p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">31-60 días</p><p className="text-lg font-bold font-mono text-amber-600">{GTQ(s.b31_60)}</p></div>
        <div className="rounded-sm border border-border p-3"><p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">+60 días</p><p className="text-lg font-bold font-mono text-red-600">{GTQ(s.b60p)}</p></div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="rounded-sm pl-9 h-9 text-sm" placeholder="Buscar cliente..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="ar-search" />
        </div>
        <Button variant="outline" className="rounded-sm gap-2 text-xs h-9" onClick={exportCSV} data-testid="ar-export"><FileDown className="h-4 w-4" /> Exportar CSV</Button>
      </div>

      <Card className="rounded-sm border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ar-table">
            <thead><tr className="bg-muted text-left text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="px-3 py-2.5 font-bold">Cliente</th><th className="px-3 py-2.5 font-bold text-right"># Fac.</th>
              <th className="px-3 py-2.5 font-bold text-right">Facturado</th><th className="px-3 py-2.5 font-bold text-right">Cobrado</th>
              <th className="px-3 py-2.5 font-bold text-right">Retenido</th>
              <th className="px-3 py-2.5 font-bold text-right">Saldo</th><th className="px-3 py-2.5 font-bold text-right">0-30</th>
              <th className="px-3 py-2.5 font-bold text-right">31-60</th><th className="px-3 py-2.5 font-bold text-right">+60</th></tr></thead>
            <tbody>
              {clients.length === 0 ? <tr><td colSpan={9} className="text-center py-10 text-muted-foreground text-sm">Sin cuentas por cobrar.</td></tr> :
                clients.map((c) => (
                  <tr key={c.client_id} className="border-t border-border hover:bg-muted/50" data-testid={`ar-row-${c.client_id}`}>
                    <td className="px-3 py-2 font-medium">{c.client_name}{c.max_overdue > 60 && <span className="ml-2 text-[10px] font-bold text-red-600">MORA {c.max_overdue}d</span>}</td>
                    <td className="px-3 py-2 text-right">{c.invoice_count}</td>
                    <td className="px-3 py-2 text-right font-mono">{GTQ(c.billed)}</td>
                    <td className="px-3 py-2 text-right font-mono text-green-700">{GTQ(c.collected)}</td>
                    <td className="px-3 py-2 text-right font-mono text-indigo-600">{c.retained > 0 ? GTQ(c.retained) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{GTQ(c.pending)}</td>
                    <td className="px-3 py-2 text-right font-mono text-green-700">{c.b0_30 > 0 ? GTQ(c.b0_30) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-600">{c.b31_60 > 0 ? GTQ(c.b31_60) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{c.b60p > 0 ? GTQ(c.b60p) : "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ReceivablesSummary;
